import { FaFolder } from 'react-icons/fa6';
import AlbumCard from './AlbumCard';
import type { Album } from '../../api/types';
import './AlbumList.scss';

interface AlbumListProps {
  albums?: Album[];
  isLoading?: boolean;
  title?: string | null;
  emptyMessage?: string | null;
  emptySubtitle?: string | null;
  /** The buyer's own opened albums: each card offers "Close album". */
  closable?: boolean;
}

/**
 * AlbumList - Reusable component for displaying a grid of albums
 *
 * @param {Array} albums - Array of album objects to display
 * @param {boolean} isLoading - Whether the albums are still loading
 * @param {string} title - Optional title for the album list section
 * @param {string} emptyMessage - Optional message to show when no albums
 * @param {string} emptySubtitle - Optional subtitle for empty state
 * @param {boolean} closable - Cards offer the "Close album" menu
 */
const AlbumList = ({
  albums = [],
  isLoading = false,
  title = null,
  emptyMessage = null,
  emptySubtitle = null,
  closable = false,
}: AlbumListProps) => {

  if (isLoading) {
    return null; // Parent component should handle loading state
  }

  if (!albums || albums.length === 0) {
    if (!emptyMessage) {
      return null; // Don't show anything if no albums and no custom empty message
    }
    return (
      <div className="albums-empty">
        <FaFolder size={48} className="empty-icon" />
        <h3 className="title is-5">{emptyMessage}</h3>
        {emptySubtitle && (
          <p className="subtitle is-6">{emptySubtitle}</p>
        )}
      </div>
    );
  }

  return (
    <div className="album-list-container">
      {title && <h2 className="section-title">{title}</h2>}
      <div className="albums-grid">
        {albums.map((album) => (
          <AlbumCard key={album.id} album={album} closable={closable} />
        ))}
      </div>
    </div>
  );
}

export default AlbumList;
